import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hidden", "results", "preview", "mediaContainer", "muscleGroup", "muscleGroupBadge", "noVideo", "muscleFilter", "equipmentFilter"]
  static values = { url: String, gifUrl: String, muscleLabel: String }

  connect() {
    this.timeout = null
    this.restorePreview()
  }

  onInput() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.fetchResults()
    }, 300)
  }

  filterByMuscle() {
    this.inputTarget.value = ""
    this.hiddenTarget.value = ""
    this.hidePreview()
    this.fetchResults()
  }

  async fetchResults() {
    const query = this.inputTarget.value
    const url = this.buildURL(query)

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html" }
      })
      const html = await response.text()
      this.resultsTarget.innerHTML = html
      this.showResults()
    } catch (error) {
      console.error("Error fetching results:", error)
    }
  }

  buildURL(query) {
    const url = new URL(this.urlValue, window.location.origin)
    if (query) url.searchParams.set("q", query)
    if (this.hasMuscleFilterTarget && this.muscleFilterTarget.value) {
      url.searchParams.set("muscle_group", this.muscleFilterTarget.value)
    }
    if (this.hasEquipmentFilterTarget && this.equipmentFilterTarget.value) {
      url.searchParams.set("equipment", this.equipmentFilterTarget.value)
    }
    return url.toString()
  }

  select(event) {
    const li = event.target.closest("li[data-autocomplete-value]")
    if (!li) return

    const value = li.dataset.autocompleteValue
    const text = li.querySelector("span")?.textContent || li.textContent.trim()
    const equipment = li.dataset.equipment

    this.hiddenTarget.value = value
    this.inputTarget.value = text
    this.inputTarget.classList.remove("border-red")
    this.inputTarget.classList.add("border-gray-border")
    this.hideResults()
    this.updatePreviewFromElement(li)
    this.clearErrorsIfValid()

    this.dispatch("equipmentChanged", { detail: { equipment, exerciseId: value } })
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
  }

  hideResults() {
    this.resultsTarget.classList.add("hidden")
  }

  onClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  updatePreviewFromElement(element) {
    const gifUrl = element.dataset.gifUrl
    const muscleGroupLabel = element.dataset.muscleGroupLabel

    this.showPreviewData(gifUrl, muscleGroupLabel)
  }

  restorePreview() {
    if (!this.hiddenTarget.value) {
      this.hidePreview()
      return
    }

    this.showPreviewData(this.gifUrlValue, this.muscleLabelValue)
  }

  showPreviewData(gifUrl, muscleGroupLabel) {
    if (gifUrl) {
      this.renderMedia(gifUrl)
      this.previewTarget.classList.remove("hidden")
      if (this.hasNoVideoTarget) this.noVideoTarget.classList.add("hidden")
    } else {
      this.previewTarget.classList.add("hidden")
      if (this.hasNoVideoTarget) this.noVideoTarget.classList.remove("hidden")
    }

    if (muscleGroupLabel) {
      this.muscleGroupBadgeTarget.textContent = muscleGroupLabel
      this.muscleGroupTarget.classList.remove("hidden")
    } else {
      this.muscleGroupTarget.classList.add("hidden")
    }
  }

  renderMedia(url) {
    const container = this.mediaContainerTarget
    container.innerHTML = ""

    if (url.endsWith(".mp4")) {
      const video = document.createElement("video")
      video.src = url
      video.autoplay = true
      video.loop = true
      video.muted = true
      video.playsInline = true
      video.className = "w-full rounded-md"
      container.appendChild(video)
    } else {
      const img = document.createElement("img")
      img.src = url
      img.alt = "Exercise demonstration"
      img.loading = "lazy"
      img.className = "w-full rounded-md"
      container.appendChild(img)
    }
  }

  clearErrorsIfValid() {
    const card = this.element
    if (!card.hasAttribute("data-has-errors")) return

    const hasExercise = this.hiddenTarget.value
    const repsField = card.querySelector("input[name*='[reps]'][type='hidden']")
    const setsField = card.querySelector("input[name*='[sets]'][type='hidden']")
    const hasReps = repsField && repsField.value && parseInt(repsField.value) > 0
    const hasSets = setsField && setsField.value && parseInt(setsField.value) > 0

    if (hasExercise && hasReps && hasSets) {
      card.removeAttribute("data-has-errors")
      card.classList.remove("bg-red-light", "border-red/40")
      card.classList.add("bg-gray-50", "border-gray-border")

      const errorBlock = card.querySelector("[data-error-block]")
      if (errorBlock) errorBlock.remove()

      this.clearBannerIfNoErrors()
    }
  }

  clearBannerIfNoErrors() {
    if (document.querySelector("[data-has-errors]")) return

    const banner = document.querySelector("[data-form-error-banner]")
    if (banner) banner.remove()
  }

  hidePreview() {
    if (this.hasPreviewTarget) this.previewTarget.classList.add("hidden")
    if (this.hasNoVideoTarget) this.noVideoTarget.classList.add("hidden")
    if (this.hasMuscleGroupTarget) this.muscleGroupTarget.classList.add("hidden")
    if (this.hasMediaContainerTarget) this.mediaContainerTarget.innerHTML = ""
  }
}